import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  Chip,
} from "@mui/material";
import { useDispatch } from "react-redux";
import { updateScreenTitle } from "../../../LayoutContainers/AppLayout/redux/AppSlice";
import AppService from "../../../Configurations/Appservice";
import { People, CheckCircle, Pending } from "@mui/icons-material";

// Helper function to format currency
const formatCurrency = (amount, currency = 'AED') => {
  if (amount === null || amount === undefined) return 'AED 0.00';
  return new Intl.NumberFormat('en-AE', { style: 'currency', currency }).format(amount);
};

// Metric Tile Component - matching CommissionDashboard design
const MetricTile = ({ title, amount, subtitle, icon: Icon, color }) => (
  <Card
    sx={{
      background: color,
      borderRadius: 2,
      p: 2.5,
      transition: 'all 0.3s ease',
      position: 'relative',
      overflow: 'hidden',
      minHeight: 130
    }}
  >
    {/* Icon in top right */}
    <Box
      sx={{
        position: 'absolute',
        top: 12,
        right: 12,
        opacity: 0.15,
        color: 'white'
      }}
    >
      <Icon sx={{ fontSize: 42 }} />
    </Box>

    {/* Content */}
    <Box sx={{ position: 'relative', zIndex: 1 }}>
      <Typography
        variant="subtitle2"
        sx={{
          color: 'rgba(255, 255, 255, 0.95)',
          fontWeight: 500,
          mb: 1.5,
          fontSize: '0.75rem'
        }}
      >
        {title}
      </Typography>

      <Typography
        variant="h4"
        sx={{
          color: '#fff',
          fontWeight: 700,
          mb: 0.75,
          fontSize: '1.4rem',
          lineHeight: 1.2
        }}
      >
        {amount}
      </Typography>

      <Typography
        variant="caption"
        sx={{
          color: 'rgba(255, 255, 255, 0.85)',
          fontSize: '0.65rem',
          display: 'flex',
          alignItems: 'center',
          gap: 0.5
        }}
      >
        {subtitle}
      </Typography>
    </Box>
  </Card>
);

const ReferredCustomers = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(updateScreenTitle("Referred Customers"));
    return () => dispatch(updateScreenTitle(""));
  }, [dispatch]);

  useEffect(() => {
    fetchReferredCustomers();
  }, []);

  const fetchReferredCustomers = async () => {
    try {
      setLoading(true);
      const authInfo = localStorage.getItem("authInfo");
      const parsedAuth = JSON.parse(authInfo);
      const partnerId = parsedAuth.principalInfo.organisationId;

      const response = await AppService.get(
        `api/channel-partners/${partnerId}/commission/referred-customers`
      );
      setCustomers(response || []);
      setError(null);
    } catch (err) {
      console.error("Error fetching referred customers:", err);
      setError("Failed to load referred customers");
      setCustomers([]);
    } finally {
      setLoading(false);
    }
  };

  // Calculate summary from API response
  const totalCustomers = customers.length;
  const totalPaidCommission = customers.reduce((sum, c) => sum + (parseFloat(c.paidCommission) || 0), 0);
  const totalPaidTickets = customers.reduce((sum, c) => sum + (c.paidTicketsCount || 0), 0);
  const totalPendingCommission = customers.reduce((sum, c) => sum + (parseFloat(c.pendingCommission) || 0), 0);
  const totalPendingTickets = customers.reduce((sum, c) => sum + (c.pendingTicketsCount || 0), 0);

  return (
    <Box sx={{ p: 3 }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Metric Tiles */}
      <Box sx={{ mb: 3 }}>
        <Grid container spacing={2}>
          {/* Total Customers */}
          <Grid item xs={12} sm={6} md={4}>
            <MetricTile
              title="Total Customers"
              amount={totalCustomers}
              subtitle="Active referred customers"
              icon={People}
              color="#1a7f8f"
            />
          </Grid>

          {/* Total Paid Commission */}
          <Grid item xs={12} sm={6} md={4}>
            <MetricTile
              title="Total Paid Commission"
              amount={formatCurrency(totalPaidCommission)}
              subtitle={`${totalPaidTickets} ticket${totalPaidTickets !== 1 ? 's' : ''} paid`}
              icon={CheckCircle}
              color="#2e7d32"
            />
          </Grid>

          {/* Total Pending Commission */}
          <Grid item xs={12} sm={6} md={4}>
            <MetricTile
              title="Total Pending Commission"
              amount={formatCurrency(totalPendingCommission)}
              subtitle={`${totalPendingTickets} ticket${totalPendingTickets !== 1 ? 's' : ''} pending`}
              icon={Pending}
              color="#ed6c02"
            />
          </Grid>
        </Grid>
      </Box>

      {/* Customers Table */}
      <Card sx={{ boxShadow: 2 }}>
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="h6" fontWeight={600}>
            Referred Customers
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Customers referred by you and their commission details
          </Typography>
        </Box>

        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: '#1a8a7e' }}>
                  <TableCell sx={{ color: 'white', fontWeight: 600 }}>Customer Name</TableCell>
                  <TableCell align="center" sx={{ color: 'white', fontWeight: 600 }}>Status</TableCell>
                  <TableCell align="right" sx={{ color: 'white', fontWeight: 600 }}>Commission %</TableCell>
                  <TableCell align="right" sx={{ color: 'white', fontWeight: 600 }}>Commission Type</TableCell>
                  <TableCell align="right" sx={{ color: 'white', fontWeight: 600 }}>Paid Tickets</TableCell>
                  <TableCell align="right" sx={{ color: 'white', fontWeight: 600 }}>Paid Commission</TableCell>
                  <TableCell align="right" sx={{ color: 'white', fontWeight: 600 }}>Pending Tickets</TableCell>
                  <TableCell align="right" sx={{ color: 'white', fontWeight: 600 }}>Pending Commission</TableCell>
                  <TableCell align="right" sx={{ color: 'white', fontWeight: 600 }}>Total Commission</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {customers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">No referred customers found</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  customers.map((customer, index) => (
                    <TableRow key={customer.customerId || index} hover>
                      <TableCell>
                        <Typography fontWeight={500}>{customer.customerName}</Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={customer.referralStatus || 'ACTIVE'}
                          color={customer.referralStatus === "ACTIVE" ? "success" : "default"}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Typography fontWeight={500}>{customer.commissionPercentage}%</Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Chip
                          label={customer.commissionType || 'PROFIT'}
                          size="small"
                          variant="outlined"
                          color="primary"
                        />
                      </TableCell>
                      <TableCell align="right">{customer.paidTicketsCount || 0}</TableCell>
                      <TableCell align="right" sx={{ color: 'success.main', fontWeight: 500 }}>
                        {formatCurrency(customer.paidCommission)}
                      </TableCell>
                      <TableCell align="right">{customer.pendingTicketsCount || 0}</TableCell>
                      <TableCell align="right" sx={{ color: 'warning.main', fontWeight: 500 }}>
                        {formatCurrency(customer.pendingCommission)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>
                        {formatCurrency(customer.totalCommission)}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Card>
    </Box>
  );
};

export default ReferredCustomers;
